import React, { useState, useMemo } from 'react';
import { useAppContext } from '../App';
import { hotProducts } from '../data/mockData';

const ProductsPage = () => {
  const { handleProductClick, addToCart, showNotification, toggleFavorite, isFavorite, addToCompare } = useAppContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [purityFilter, setPurityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');

  const categories = ['all', 'Surfactant', 'Solvent', 'Inorganic', 'Organic'];
  const purityOptions = ['all', '99.9%', '99.5%', '99%', '98%', '95%'];

  const filteredProducts = useMemo(() => {
    let products = [...hotProducts];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      products = products.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.casNumber?.toLowerCase().includes(query) ||
        p.supplier?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      products = products.filter(p => p.category === selectedCategory);
    }

    // Purity filter
    if (purityFilter !== 'all') {
      const minPurity = parseFloat(purityFilter);
      products = products.filter(p => parseFloat(p.purity) >= minPurity);
    }

    // Sort
    products.sort((a, b) => {
      switch (sortBy) {
        case 'name': return a.name.localeCompare(b.name);
        case 'purity': return parseFloat(b.purity) - parseFloat(a.purity);
        default: return 0;
      }
    });

    return products;
  }, [searchQuery, selectedCategory, purityFilter, sortBy]);

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    addToCart(product, 100);
  };

  const handleToggleFavorite = (product, e) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  const handleAddToCompare = (product, e) => {
    e.stopPropagation();
    addToCompare(product);
  };

  return (
    <div className="products-page">
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">Chemical Database</h1>
            <p className="page-subtitle">
              Browse our comprehensive catalog of {hotProducts.length * 100}+ industrial chemicals
            </p>
          </div>
          <div className="header-stats">
            <div className="stat">
              <span className="stat-value">{hotProducts.length}</span>
              <span className="stat-label">Products</span>
            </div>
            <div className="stat">
              <span className="stat-value">5</span>
              <span className="stat-label">Categories</span>
            </div>
            <div className="stat">
              <span className="stat-value">99%+</span>
              <span className="stat-label">Purity</span>
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="filters-bar">
          <div className="search-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, CAS number, or supplier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery('')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="filter-group">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat === 'all' ? 'All Categories' : cat}</option>
              ))}
            </select>

            <select value={purityFilter} onChange={(e) => setPurityFilter(e.target.value)}>
              {purityOptions.map(purity => (
                <option key={purity} value={purity}>{purity === 'all' ? 'All Purity' : `≥ ${purity}`}</option>
              ))}
            </select>

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Sort by Name</option>
              <option value="purity">Highest Purity</option>
            </select>
          </div>

          <div className="view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="4" width="18" height="4" rx="1" />
                <rect x="3" y="10" width="18" height="4" rx="1" />
                <rect x="3" y="16" width="18" height="4" rx="1" />
              </svg>
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="results-info">
          <span>{filteredProducts.length} products found</span>
          {(selectedCategory !== 'all' || purityFilter !== 'all' || searchQuery) && (
            <button
              className="clear-filters"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPurityFilter('all');
              }}
            >
              Clear all filters
            </button>
          )}
        </div>

        {/* Products Grid/List */}
        {filteredProducts.length > 0 ? (
          <div className={`products-${viewMode}`}>
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => handleProductClick(product)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="product-image">
                  <img src={product.image} alt={product.name} />
                  <div className="product-overlay">
                    <button
                      className={`action-btn favorite ${isFavorite(product.id) ? 'active' : ''}`}
                      onClick={(e) => handleToggleFavorite(product, e)}
                      title="Add to favorites"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite(product.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </button>
                    <button
                      className="action-btn compare"
                      onClick={(e) => handleAddToCompare(product, e)}
                      title="Add to compare"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                    </button>
                  </div>
                  <span className="category-badge">{product.category}</span>
                </div>

                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <p className="product-cas">CAS: {product.casNumber}</p>

                  <div className="product-meta">
                    <div className="meta-item">
                      <span className="label">Purity</span>
                      <span className="value">{product.purity}</span>
                    </div>
                    <div className="meta-item">
                      <span className="label">Supplier</span>
                      <span className="value">{product.supplier}</span>
                    </div>
                  </div>

                  <div className="product-footer">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={(e) => handleAddToCart(product, e)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="9" cy="21" r="1" />
                        <circle cx="20" cy="21" r="1" />
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                      </svg>
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <h3>No products found</h3>
            <p>Try adjusting your search or filters</p>
            <button
              className="btn btn-outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPurityFilter('all');
              }}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        .products-page {
          padding: var(--space-xl) 0 var(--space-3xl);
          min-height: 100vh;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--space-xl);
          padding-bottom: var(--space-xl);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .page-title {
          font-size: var(--font-4xl);
          font-weight: 700;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-sm);
        }

        .page-subtitle {
          color: var(--color-neutral-400);
          font-size: var(--font-lg);
        }

        .header-stats {
          display: flex;
          gap: var(--space-xl);
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border-radius: var(--radius-lg);
          min-width: 80px;
        }

        .stat-value {
          font-size: var(--font-2xl);
          font-weight: 700;
          color: var(--color-accent-400);
        }

        .stat-label {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
          text-transform: uppercase;
        }

        .filters-bar {
          display: flex;
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
          flex-wrap: wrap;
          align-items: center;
          padding: var(--space-md);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-lg);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .search-box {
          flex: 1;
          min-width: 250px;
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md);
        }

        .search-box svg {
          color: var(--color-neutral-500);
        }

        .search-box input {
          flex: 1;
          background: none;
          border: none;
          color: var(--color-neutral-100);
          font-size: var(--font-sm);
          outline: none;
        }

        .search-box input::placeholder {
          color: var(--color-neutral-500);
        }

        .clear-btn {
          background: none;
          border: none;
          color: var(--color-neutral-500);
          cursor: pointer;
          padding: 2px;
        }

        .clear-btn:hover {
          color: var(--color-neutral-300);
        }

        .filter-group {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
        }

        .filter-group select {
          padding: var(--space-sm) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md);
          color: var(--color-neutral-200);
          font-size: var(--font-sm);
          cursor: pointer;
        }

        .filter-group select:focus {
          outline: none;
          border-color: var(--color-accent-400);
        }

        .view-toggle {
          display: flex;
          gap: 4px;
          padding: 4px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: var(--radius-md);
        }

        .view-toggle button {
          padding: var(--space-sm);
          background: transparent;
          border: none;
          color: var(--color-neutral-500);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: all 150ms ease;
        }

        .view-toggle button.active {
          background: var(--color-accent-500);
          color: white;
        }

        .results-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-md);
          color: var(--color-neutral-400);
          font-size: var(--font-sm);
        }

        .clear-filters {
          background: none;
          border: none;
          color: var(--color-accent-400);
          cursor: pointer;
          font-size: var(--font-sm);
        }

        .clear-filters:hover {
          text-decoration: underline;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: var(--space-lg);
        }

        .products-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }

        .product-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
          overflow: hidden;
          cursor: pointer;
          transition: all var(--transition-base);
          animation: fadeInUp 0.4s ease forwards;
          opacity: 0;
        }

        .product-card:hover {
          transform: translateY(-4px);
          border-color: var(--color-accent-400);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }

        .product-image {
          position: relative;
          height: 180px;
          background: rgba(0, 0, 0, 0.3);
        }

        .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform var(--transition-base);
        }

        .product-card:hover .product-image img {
          transform: scale(1.05);
        }

        .product-overlay {
          position: absolute;
          inset: 0;
          background: rgba(15, 23, 42, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-sm);
          opacity: 0;
          transition: opacity var(--transition-fast);
        }

        .product-card:hover .product-overlay {
          opacity: 1;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 50%;
          color: white;
          cursor: pointer;
          transition: all 150ms ease;
        }

        .action-btn:hover {
          background: var(--color-accent-500);
        }

        .action-btn.favorite.active {
          color: #ef4444;
        }

        .category-badge {
          position: absolute;
          top: var(--space-sm);
          left: var(--space-sm);
          padding: 4px 10px;
          background: rgba(59, 130, 246, 0.9);
          color: white;
          font-size: var(--font-xs);
          font-weight: 500;
          border-radius: var(--radius-sm);
          text-transform: uppercase;
        }

        .product-info {
          padding: var(--space-md);
        }

        .product-name {
          font-size: var(--font-base);
          font-weight: 600;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-xs);
        }

        .product-cas {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
          font-family: var(--font-mono);
          margin-bottom: var(--space-sm);
        }

        .product-meta {
          display: flex;
          gap: var(--space-md);
          margin-bottom: var(--space-md);
          padding-bottom: var(--space-sm);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .meta-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .meta-item .label {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
        }

        .meta-item .value {
          font-size: var(--font-xs);
          color: var(--color-neutral-300);
        }

        .product-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .product-price {
          font-size: var(--font-lg);
          font-weight: 700;
          color: var(--color-accent-300);
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: var(--font-xs);
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .no-results {
          text-align: center;
          padding: var(--space-3xl);
          color: var(--color-neutral-400);
        }

        .no-results svg {
          margin-bottom: var(--space-lg);
          opacity: 0.5;
        }

        .no-results h3 {
          color: var(--color-neutral-200);
          margin-bottom: var(--space-sm);
        }

        .no-results p {
          margin-bottom: var(--space-lg);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--space-lg);
          }

          .header-stats {
            width: 100%;
            justify-content: space-around;
          }

          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            min-width: 100%;
          }

          .filter-group {
            width: 100%;
          }

          .filter-group select {
            flex: 1;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          }
        }
      `}</style>
    </div>
  );
};

export default ProductsPage;
